import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ActivityDto, FileDto, ProjectDto, ProjectStatus, ProjectSummaryDto, TagDto } from '@cw/shared';
import { api } from './api';
import { useCan } from './session';

export const projectKeys = {
  all: ['projects'] as const,
  list: (params: string) => ['projects', 'list', params] as const,
  detail: (id: string) => ['projects', id] as const,
  activity: (id: string) => ['projects', id, 'activity'] as const,
  files: (id: string) => ['projects', id, 'files'] as const,
  tags: ['tags'] as const,
};

export type ProjectFilters = {
  q?: string;
  status?: ProjectStatus | 'open' | 'all';
  clientId?: string;
  mine?: boolean;
};

export function useProjects(f: ProjectFilters = {}) {
  const params = new URLSearchParams();
  if (f.q) params.set('q', f.q);
  if (f.status) params.set('status', f.status);
  if (f.clientId) params.set('clientId', f.clientId);
  if (f.mine) params.set('mine', '1');
  const qs = params.toString();
  return useQuery({
    queryKey: projectKeys.list(qs),
    queryFn: () => api.get<ProjectSummaryDto[]>(`/projects?${qs}`),
    enabled: useCan('projects:read'),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => api.get<ProjectDto>(`/projects/${id}`),
  });
}

export function useTags() {
  return useQuery({
    queryKey: projectKeys.tags,
    queryFn: () => api.get<TagDto[]>('/tags'),
    staleTime: 60_000,
  });
}

export function useProjectFiles(id: string) {
  return useQuery({
    queryKey: projectKeys.files(id),
    queryFn: () => api.get<FileDto[]>(`/files?projectId=${id}&purpose=project_file`),
    enabled: useCan('files:read'),
  });
}

/** Feed de atividade paginado de qualquer recurso que exponha `GET …/activity?before=`. */
export function useActivityFeed(key: readonly unknown[], basePath: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) =>
      api.get<{ items: ActivityDto[]; nextBefore: string | null }>(
        `${basePath}/activity${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled,
  });
}

export function formatMoney(cents: number | null | undefined, currency = 'BRL'): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** "2026-10-30" → "30 out" (ou "30 out 2027" se for outro ano). Datas sem fuso: não converter para UTC. */
export function formatShortDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y!, m! - 1, d!);
  const sameYear = y === new Date().getFullYear();
  return date
    .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })
    .replace('.', '');
}

export function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const due = new Date(y!, m! - 1, d!);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}
